import { useState } from 'react';

/**
 * Starting size for an empty round: a 6 × 5 classic board or custom steppers, tiles
 * valued `100 × (tier + 1)` and written blank.
 */

const JEOPARDY_COLUMNS = 6;
const JEOPARDY_ROWS = 5;
const MIN_COLUMNS = 1;
const MAX_COLUMNS = 10;
const MIN_ROWS = 1;
const MAX_ROWS = 8;

interface Props {
  /** Bulk-create `columns` empty categories, each with `rows` blank tiles. */
  onCreate: (columns: number, rows: number) => Promise<void>;
}

function Stepper({
  label,
  value,
  min,
  max,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  disabled: boolean;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  return (
    <div className="field size-stepper">
      <label>{label}</label>
      <div className="row" style={{ alignItems: 'center', gap: '0.4rem' }}>
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled || value <= min}
          aria-label={`Fewer ${label.toLowerCase()}`}
          onClick={() => onChange(clamp(value - 1))}
        >
          −
        </button>
        <strong style={{ minWidth: '1.5rem', textAlign: 'center' }}>{value}</strong>
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled || value >= max}
          aria-label={`More ${label.toLowerCase()}`}
          onClick={() => onChange(clamp(value + 1))}
        >
          +
        </button>
      </div>
    </div>
  );
}

export function BoardQuickStart({ onCreate }: Props) {
  const [columns, setColumns] = useState(JEOPARDY_COLUMNS);
  const [rows, setRows] = useState(JEOPARDY_ROWS);
  const [busy, setBusy] = useState(false);

  async function create(cols: number, tiers: number) {
    if (busy) return;
    setBusy(true);
    try {
      await onCreate(cols, tiers);
      // On success the round is no longer empty, so this component unmounts. On failure
      // (a toast the host has already seen) re-enable so they can try again.
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel col board-quickstart">
      <h2 className="panel-title" style={{ margin: 0 }}>
        Start the board
      </h2>
      <p className="faint" style={{ margin: 0 }}>
        Lay out the shape now and write the clues after — or press + on the board to build
        it up one tile at a time.
      </p>

      <div className="row board-quickstart-options">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={() => void create(JEOPARDY_COLUMNS, JEOPARDY_ROWS)}
        >
          Classic board — {JEOPARDY_COLUMNS} × {JEOPARDY_ROWS}
        </button>
      </div>

      <div className="board-quickstart-custom">
        <span className="faint">or a custom board</span>
        <div className="row" style={{ alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
          <Stepper
            label="Categories"
            value={columns}
            min={MIN_COLUMNS}
            max={MAX_COLUMNS}
            onChange={setColumns}
            disabled={busy}
          />
          <Stepper label="Tiers" value={rows} min={MIN_ROWS} max={MAX_ROWS} onChange={setRows} disabled={busy} />
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => void create(columns, rows)}
          >
            {busy ? 'Creating…' : `Create ${columns} × ${rows}`}
          </button>
        </div>
      </div>
    </div>
  );
}
